import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight, Loader2, RotateCcw, Trophy } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { PlayingCard } from "../PlayingCard";
import { RankChange } from "../rank/RankChange";
import { playerInitial } from "../../lib/playerColors";
import { sortGroupCards } from "../../lib/cardSort";
import { MEDALS, gameWinners, penaltyLines, scoreBar, standings } from "../../lib/roundSummary";
import { t, tRich, trServer, textDir } from "../../lib/i18n";

/**
 * The round's recap, the same at the end of the game. It fits a phone's screen with four players (owner's request,
 * November 2026: it took a long scroll to find one's own score, under a button floating over the cards): who won in
 * the header, then a row per player with their score on the way to elimination and the cards that made it. The
 * actions stay in a bar under the rows: « Manche suivante » for the host alone, who the table waits for for the others.
 */

// The player's own hand as it was on their screen: the manual order, or the sort they had chosen.
function inHandOrder(cards, order) {
  if (!order || order.length === 0) return cards;
  return [...cards].sort((a, b) => {
    const ia = order.indexOf(a.id);
    const ib = order.indexOf(b.id);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

const comboLabel = (kind) =>
  kind === "sequence" || kind === "set" ? t(`summary.kind.${kind}`) : kind;

const Label = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wide text-slate-400">{children}</div>
);

// Cards read left-to-right in every language; under an RTL dialog the row is only packed to the right.
const CARD_ROW = "flex rtl:justify-end";

// A hand, laid out like the groups (owner's request): each card over the one before, 20 px of it in sight. Only when
// the row is short of room (14 cards on a narrow phone) do they close up further: all but the last can shrink.
const CardFan = ({ cards }) => (
  <div dir="ltr" className={`${CARD_ROW} mt-1`}>
    {cards.map((c, i) => (
      <div key={c.id} className={i === cards.length - 1 ? "shrink-0" : "min-w-0 shrink basis-5"}>
        <PlayingCard card={c} size="xs" rounded="sm" disabled readOnly testId={`summary-card-${c.id}`} />
      </div>
    ))}
  </div>
);

// Groups on the table, the cards added later ringed in gold. A group's cards overlap: its rank and suit still read.
const ComboGroups = ({ combos, withLabels = false, children }) => (
  <div className="mt-1 flex flex-wrap items-end gap-x-2.5 gap-y-1.5">
    {combos.map((combo) => {
      const added = new Set(combo.added_card_ids || []);
      const cards = combo.kind === "set" ? sortGroupCards("set", combo.cards) : combo.cards;
      return (
        <div key={combo.id}>
          {withLabels && (
            <div className="mb-0.5 text-[9px] uppercase text-slate-500">
              {comboLabel(combo.kind)} · {t("common.pts", { n: combo.points })}
            </div>
          )}
          <div dir="ltr" className="flex -space-x-3">
            {cards.map((c) => (
              <div key={c.id} className={added.has(c.id) ? "rounded-sm ring-1 ring-amber-400" : undefined}>
                <PlayingCard card={c} size="xs" rounded="sm" disabled readOnly />
              </div>
            ))}
          </div>
        </div>
      );
    })}
    {children}
  </div>
);

const BAR_TONE = { safe: "bg-amber-300/80", warning: "bg-orange-400", danger: "bg-rose-400" };

// The score on the way to the limit: what it held before, then what this round added, growing in once.
function ScoreBar({ before, after, limit }) {
  const reduceMotion = useReducedMotion();
  const bar = scoreBar(before, after, limit);
  const added = `${bar.added * 100}%`;
  return (
    <div className="mt-1 flex h-1 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
      <div className={BAR_TONE[bar.level]} style={{ width: `${bar.before * 100}%` }} />
      <motion.div
        className="bg-rose-300"
        initial={reduceMotion ? { width: added } : { width: 0 }}
        animate={{ width: added }}
        transition={{ duration: 0.5, delay: 0.25, ease: "easeOut" }}
      />
    </div>
  );
}

const Badge = ({ tone, children }) => (
  <span className={`shrink-0 whitespace-nowrap rounded-full px-1.5 py-px text-[10px] leading-4 ${tone}`}>{children}</span>
);

function SummaryRow({ row, player, mine, limit, handOrder, index, medal, forced }) {
  const reduceMotion = useReducedMotion();
  // A winner of the game whom its last round took past the limit (the other stayed in) is still a winner.
  const out = !!row.previously_eliminated && !medal;
  const justOut = !out && !!player?.eliminated && !medal;
  const hand = mine ? inHandOrder(row.remaining_hand || [], handOrder) : row.remaining_hand || [];
  // The winner shows the groups that won, no hand (owner's request). A round won by the others' elimination has no
  // winning move: that winner still holds their cards.
  const showHand = hand.length > 0 && (!row.is_winner || forced);
  const { cards, lines } = penaltyLines(row);
  const frame = row.is_winner || medal
    ? "border-amber-300/60 bg-amber-300/[0.07]"
    : justOut
    ? "border-rose-400/50 bg-rose-500/10"
    : mine
    ? "border-white/25 bg-white/[0.08]"
    : "border-white/5 bg-slate-800/60";

  return (
    <motion.li
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: index * 0.05 }}
      className={`rounded-xl border px-2.5 py-1.5 ${frame} ${out ? "opacity-50" : ""}`}
      data-testid={`summary-player-${row.user_id}`}
    >
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarImage src={player?.picture} />
          <AvatarFallback className="bg-emerald-900 text-xs text-amber-300">
            {playerInitial(player || { name: row.name })}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {medal && <span className="shrink-0 text-base leading-none" data-testid="summary-medal">{medal}</span>}
            <span className="min-w-0 truncate text-sm" dir="auto">{row.name}</span>
            {row.is_winner && <Badge tone="bg-amber-500/20 text-amber-300">{t("summary.nefrechBadge")}</Badge>}
            {(out || justOut) && <Badge tone="bg-rose-500/25 text-rose-200">{t("common.eliminated")}</Badge>}
            {!row.has_played_nemna3 && !row.is_winner && !out && !justOut && (
              <Badge tone="bg-rose-500/20 text-rose-300">{t("summary.notPosed")}</Badge>
            )}
          </div>
          <ScoreBar before={row.score_before} after={row.score_after} limit={limit} />
        </div>
        <div dir="ltr" className="shrink-0 text-end leading-none" data-testid="summary-score">
          {(!out || row.round_penalty > 0) && (
            <div className={`text-xs ${row.round_penalty > 0 ? "text-rose-300" : "text-emerald-300"}`}>+{row.round_penalty}</div>
          )}
          <div className="mt-0.5">
            <span className="font-display text-xl leading-none text-amber-300">{row.score_after}</span>
            <span className="text-[10px] text-slate-500">/{limit}</span>
          </div>
        </div>
      </div>

      {/* The winner: the groups that won, and the last card. */}
      {row.is_winner && row.winner_combinations?.length > 0 && (
        <div className="mt-1.5">
          <Label>{t("summary.winningCombos")}</Label>
          <ComboGroups combos={row.winner_combinations}>
            {row.winner_discard && (
              <div className="flex items-center gap-1.5">
                <span className="max-w-[4.5rem] text-[10px] leading-tight text-slate-400">{t("summary.discarded")}</span>
                <PlayingCard card={row.winner_discard} size="xs" rounded="sm" disabled readOnly />
              </div>
            )}
          </ComboGroups>
        </div>
      )}

      {/* The last state of every hand: the cards they were left with, a player eliminated during the round included. */}
      {showHand && (
        <div className="mt-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <Label>{t("summary.remaining", { n: hand.length })}</Label>
            {cards > 0 && <span dir="ltr" className="text-[11px] text-rose-300">+{cards}</span>}
          </div>
          <CardFan cards={hand} />
        </div>
      )}

      {/* What the cards alone do not explain: no pose, a Joker in hand, a penalty. */}
      {lines.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] leading-snug text-slate-300">
          {lines.map((l) => (
            <li key={`${l.type}:${l.label}`} className="flex justify-between gap-3">
              <span>{trServer(l.label)}{l.count > 1 && <span dir="ltr"> ×{l.count}</span>}</span>
              <span dir="ltr" className="shrink-0 text-rose-300">+{l.points}</span>
            </li>
          ))}
        </ul>
      )}

      {/* The groups a player had posed stay a tap away: they were on the table a moment ago. */}
      {!row.is_winner && row.combinations?.length > 0 && (
        <details className="mt-1.5" data-testid="summary-posed">
          <summary className="cursor-pointer select-none text-[11px] text-slate-400 marker:text-amber-400/70">
            {t("summary.posedCombos")} ({row.combinations.length})
          </summary>
          <ComboGroups combos={row.combinations} withLabels />
        </details>
      )}
    </motion.li>
  );
}

// The host starts the next round; the others see who they are waiting for, in the button's room.
function NextRound({ isHost, hostName, onNextRound }) {
  const [busy, setBusy] = useState(false);
  if (!isHost) {
    return (
      <p
        className="flex min-h-11 items-center justify-center gap-2 text-center text-[13px] text-slate-300"
        data-testid="next-round-wait"
      >
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-amber-300" />
        <span className="min-w-0">{t("summary.waitHost", { name: hostName || t("summary.host") })}</span>
      </p>
    );
  }
  const start = async () => {
    setBusy(true);
    try {
      await onNextRound();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid="next-round-btn"
      onClick={start}
      disabled={busy}
      className="h-11 w-full rounded-full bg-amber-500 text-[15px] text-slate-950 hover:bg-amber-400"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <>
          {t("summary.nextRound")}
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </>
      )}
    </Button>
  );
}

function Recap({ game, room, user, myUserId, handOrder, onNextRound, onBackToLobby, onRematch, rematching, rematchHint }) {
  const over = game.phase === "game_over";
  const nameOf = (id) => game.players.find((p) => p.user_id === id)?.name;
  const winner = (id, key) => tRich(key, { name: <span className="text-amber-300">{nameOf(id)}</span> });
  const winners = over ? gameWinners(game) : [];
  // Two winners at a table of 4: « 🥇 Leïla · 🥈 Sami », their row marked the same way.
  const medalOf = (id) => (winners.length > 1 && winners.includes(id) ? MEDALS[winners.indexOf(id)] : null);
  const headline = over
    ? winners.length > 1
      ? (
        <span data-testid="summary-podium">
          {winners.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 && <span className="mx-1.5 text-slate-500">·</span>}
              {MEDALS[i]} <bdi className="text-amber-300">{nameOf(id)}</bdi>
            </React.Fragment>
          ))}
        </span>
      )
      : game.game_winner_id && winner(game.game_winner_id, "summary.gameWin")
    : game.winner_id
    ? winner(game.winner_id, "summary.roundWin")
    : t("summary.noWinner");

  return (
    <>
      <DialogHeader className="shrink-0 space-y-0.5 px-4 pb-2 pt-3 sm:text-center">
        <DialogTitle className="font-display text-[22px] leading-tight text-amber-300">
          {over ? t("summary.gameOver") : t("summary.roundOver", { n: game.round_number })}
        </DialogTitle>
        {headline && (
          <div className="flex items-center justify-center gap-1.5 text-sm text-slate-100" data-testid="summary-headline">
            {!over && game.winner_id && <Trophy className="h-4 w-4 shrink-0 text-amber-400" />}
            <span className="min-w-0">{headline}</span>
          </div>
        )}
      </DialogHeader>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-2.5 pb-2.5" data-testid="summary-rows">
        {/* The player's game is over (out, or the end): what it won or lost them for their rank. */}
        {game.rank_results?.[myUserId] && <RankChange result={game.rank_results[myUserId]} />}
        <ol className="space-y-1.5">
          {standings(game.round_summary?.rows, winners).map((row, i) => (
            <SummaryRow
              key={row.user_id}
              row={row}
              player={game.players.find((p) => p.user_id === row.user_id)}
              mine={row.user_id === myUserId}
              limit={game.elim_score_y}
              handOrder={handOrder}
              index={i}
              medal={medalOf(row.user_id)}
              forced={!!game.round_summary?.forced}
            />
          ))}
        </ol>
      </div>

      {/* Under the rows, never over them, and always on screen. The only way to close the dialog. */}
      <div className="shrink-0 border-t border-white/10 bg-slate-950/50 px-3 py-2">
        {game.phase === "round_over" && (
          <NextRound
            isHost={room?.host_user_id === user?.user_id}
            hostName={nameOf(room?.host_user_id)}
            onNextRound={onNextRound}
          />
        )}
        {over && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {rematchHint && (
              <span
                dir={textDir(rematchHint)}
                data-testid="rematch-hint"
                className="w-full rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-center text-xs text-emerald-300"
              >
                {rematchHint}
              </span>
            )}
            <Button
              onClick={onBackToLobby}
              disabled={rematching}
              data-testid="back-to-lobby-btn"
              className="flex-1 rounded-full border border-white/10 bg-slate-800 text-slate-200 hover:bg-slate-700"
            >
              {t("summary.backLobby")}
            </Button>
            <Button
              onClick={onRematch}
              disabled={rematching}
              data-testid="rematch-btn"
              className="flex-1 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400"
            >
              <RotateCcw className="h-4 w-4" />
              {t("common.rematch")}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export const RoundSummaryDialog = ({ open, onOpenChange, ...recap }) => (
  <Dialog open={open} onOpenChange={() => {}}>
    <DialogContent
      // As wide as a hand of 14 cards (20 px each, the last whole) with the rows' padding: they fill the width.
      className="flex max-h-[90vh] w-[calc(100%-16px)] max-w-[340px] flex-col gap-0 overflow-hidden rounded-2xl border-amber-300/20 bg-slate-900 p-0 text-white outline-none sm:rounded-2xl [&>button]:hidden"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      onOpenAutoFocus={(e) => e.preventDefault()} // no focus ring on the first thing a tap could reach
      aria-describedby={undefined}
      data-testid="round-summary"
    >
      <Recap {...recap} />
    </DialogContent>
  </Dialog>
);

export default RoundSummaryDialog;
